import React, { useState, useEffect, useCallback } from "react";
import { 
  CheckCircle2, XCircle, Clock, RefreshCw, 
  Eye, Play, Zap, ClipboardCheck, Package, Info
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import apiClient from "../../lib/api";

const IFoodHomologationTab = ({ storeId, config }) => {
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [checklistItems, setChecklistItems] = useState([
    { id: "webhook_receive", label: "Recebe eventos via webhook", status: "PENDING" },
    { id: "confirm_delivery_immediate", label: "Confirmar DELIVERY IMMEDIATE", status: "PENDING" },
    { id: "confirm_delivery_scheduled", label: "Confirmar DELIVERY SCHEDULED", status: "PENDING" },
    { id: "cancel_with_reasons", label: "Cancelar com motivos", status: "PENDING" },
    { id: "confirm_takeout_ready", label: "Confirmar TAKEOUT", status: "PENDING" },
    { id: "dedupe_events", label: "Deduplicar eventos", status: "PENDING" },
    { id: "token_renewal", label: "Token renova corretamente", status: "PENDING" },
  ]);

  const loadWebhookEvents = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/ifood/homologation/webhook-events/${storeId}?limit=50`);
      setWebhookEvents(res.data || []);
    } catch (err) {
      console.error("Error loading events:", err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const loadHomologStatus = useCallback(async () => {
    if (!storeId) return;
    try {
      const res = await apiClient.get(`/ifood/homologation/status/${storeId}`);
      if (res.data?.checklist) {
        setChecklistItems(prev => prev.map(item => {
          if (res.data.checklist[item.id]) {
            return { ...item, status: res.data.checklist[item.id].status || "PENDING" };
          }
          return item;
        }));
      }
    } catch (err) {
      console.error("Error loading status:", err);
    }
  }, [storeId]);

  useEffect(() => {
    if (storeId) {
      loadWebhookEvents();
      loadHomologStatus();
    }
  }, [storeId, loadWebhookEvents, loadHomologStatus]);

  const handleTestWebhook = async () => {
    try {
      const res = await apiClient.post(`/ifood/homologation/test-webhook/${storeId}`);
      toast.success(res.data?.success ? "Webhook OK!" : "Teste executado");
      loadWebhookEvents();
    } catch (err) {
      toast.error("Erro ao testar webhook");
    }
  };

  const handleReprocessEvent = async (eventId) => {
    try {
      await apiClient.post(`/ifood/homologation/reprocess-event/${eventId}`);
      toast.success("Evento reenviado");
      loadWebhookEvents();
    } catch (err) {
      toast.error("Erro ao reprocessar");
    }
  };

  const checklistStats = {
    total: checklistItems.length,
    passed: checklistItems.filter(i => i.status === "PASS").length,
    pending: checklistItems.filter(i => i.status === "PENDING").length,
  };

  if (!storeId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Selecione uma loja</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Homologacao iFood</h2>
          <p className="text-gray-500 text-sm">Checklist e testes para validacao</p>
        </div>
        {config?.configured && (
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              Token: {config.auth_status === "AUTHORIZED" ? "Valido" : "Pendente"}
            </span>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progresso</span>
          <span className="text-sm text-gray-500">{checklistStats.passed}/{checklistStats.total}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-green-500 h-3 rounded-full transition-all"
            style={{ width: `${(checklistStats.passed / checklistStats.total) * 100}%` }}
          />
        </div>
      </div>

      {/* Checklist */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-red-500" />
            Checklist
          </h3>
          <Button onClick={loadHomologStatus} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />Atualizar
          </Button>
        </div>
        <div className="space-y-2">
          {checklistItems.map((item, idx) => (
            <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-gray-400 font-mono text-sm w-6">{idx + 1}.</span>
                <span className="text-gray-700">{item.label}</span>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                item.status === "PASS" ? "bg-green-100 text-green-700" :
                item.status === "FAIL" ? "bg-red-100 text-red-700" :
                "bg-yellow-100 text-yellow-700"
              }`}>
                {item.status === "PASS" ? "OK" : item.status === "FAIL" ? "FALHOU" : "PENDENTE"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Webhook Events */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-500" />
            Eventos Webhook
          </h3>
          <div className="flex gap-2">
            <Button onClick={handleTestWebhook} variant="outline" size="sm">
              <Zap className="w-4 h-4 mr-2" />Testar
            </Button>
            <Button onClick={loadWebhookEvents} variant="outline" size="sm" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
          </div>
        </div>
        
        {webhookEvents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Zap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Nenhum evento recebido</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Event ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Codigo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Recebido</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {webhookEvents.slice(0, 20).map(event => (
                  <tr key={event.id || event.event_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{(event.event_id || "").slice(0, 8) || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        event.event_code === "PLACED" ? "bg-yellow-100 text-yellow-800" :
                        event.event_code === "CONFIRMED" ? "bg-blue-100 text-blue-800" :
                        "bg-gray-100 text-gray-800"
                      }`}>{event.event_code || event.event_type || "-"}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {event.received_at ? new Date(event.received_at).toLocaleString("pt-BR") : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        event.status === "PROCESSED" ? "bg-green-100 text-green-700" :
                        event.status === "FAILED" ? "bg-red-100 text-red-700" :
                        "bg-gray-100 text-gray-700"
                      }`}>{event.status === "PROCESSED" ? "OK" : event.status || "-"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setSelectedEvent(event)}>
                          <Eye className="w-3 h-3" />
                        </Button>
                        {event.status === "FAILED" && (
                          <Button size="sm" variant="ghost" onClick={() => handleReprocessEvent(event.id)}>
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Token Info */}
      {config?.configured && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <Info className="w-4 h-4" />Info do Token
          </h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-blue-600">Status:</span> <span className="text-blue-900">{config.auth_status}</span></div>
            <div><span className="text-blue-600">Expira:</span> <span className="text-blue-900">{config.token_expires_at ? new Date(config.token_expires_at).toLocaleString("pt-BR") : "-"}</span></div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedEvent(null)}>
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <h4 className="text-lg font-semibold mb-4">Payload do Evento</h4>
            <pre className="bg-gray-100 p-4 rounded-lg text-xs overflow-auto max-h-96">
              {JSON.stringify(selectedEvent.body_json ? JSON.parse(selectedEvent.body_json) : selectedEvent, null, 2)}
            </pre>
            <Button className="mt-4" onClick={() => setSelectedEvent(null)}>Fechar</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IFoodHomologationTab;
